import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const affiliateCode = searchParams.get('code');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!affiliateCode) {
      setError('Kein Affiliate-Code angegeben');
      setLoading(false);
      return;
    }

    fetchDashboardData();
  }, [affiliateCode]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/affiliate/dashboard/${affiliateCode}`);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          textArea.remove();
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          textArea.remove();
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const getCommissionTierColor = (tier) => {
    if (tier === '20%') return 'text-green-600';
    if (tier === '15%') return 'text-blue-600';
    return 'text-gray-600';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Fehler</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/affiliate/register')}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Zur Registrierung
          </button>
        </div>
      </div>
    );
  }

  const { affiliate, customers, active_customers_count, commissions, current_month_earnings } = data;
  const affiliateLink = `${window.location.origin}/register?ref=${affiliate.affiliate_code}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Willkommen, {affiliate.name}! 👋</h1>
              <p className="text-base text-gray-600 dark:text-gray-400">Ihr Affiliate-Dashboard</p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-sm text-gray-500">Ihr Code</div>
              <div className="text-xl font-bold text-blue-600">{affiliate.affiliate_code}</div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktive Kunden</h3>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{active_customers_count}</div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Provisions-Stufe</h3>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className={`text-3xl font-bold ${getCommissionTierColor(affiliate.commission_tier)}`}>
              {affiliate.commission_tier}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamt-Verdienst</h3>
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(affiliate.total_earnings)}</div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Dieser Monat</h3>
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(current_month_earnings)}</div>
          </div>
        </div>

        {/* Affiliate Link */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">🔗 Ihr Affiliate-Link</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Teilen Sie diesen Link, um neue Kunden zu gewinnen:</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={affiliateLink}
              readOnly
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm"
            />
            <button
              onClick={() => copyToClipboard(affiliateLink)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Kopiert!
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Kopieren
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Commissions */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">💰 Aktuelle Provisionen</h2>
            {commissions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Noch keine Provisionen vorhanden</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {commissions.map((commission) => (
                  <div key={commission.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(commission.amount)}</div>
                        <div className="text-xs text-gray-500">Rate: {commission.commission_rate}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        commission.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {commission.status === 'paid' ? 'Bezahlt' : 'Ausstehend'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Periode: {commission.period_month} • {formatDate(commission.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customers */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">👥 Ihre Kunden</h2>
            {customers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Noch keine Kunden vorhanden</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {customers.map((customer) => (
                  <div key={customer.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Kunde #{customer.customer_id.slice(0, 8)}</div>
                        <div className="text-xs text-gray-500">Seit: {formatDate(customer.created_at)}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        customer.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {customer.status === 'active' ? 'Aktiv' : customer.status === 'churned' ? 'Gekündigt' : 'Abgelaufen'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Provisionen: {formatCurrency(customer.total_commissions_paid)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mt-6 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">ℹ️ Wie funktioniert das Programm?</h3>
          <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            <li>• Teilen Sie Ihren Link mit potenziellen Kunden</li>
            <li>• Sie erhalten eine wiederkehrende Provision für jeden zahlenden Kunden</li>
            <li>• Ihre Provisionsstufe steigt automatisch mit der Anzahl aktiver Kunden</li>
            <li>• Provisionen werden monatlich berechnet und ausbezahlt</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;